import React, { useEffect, useState } from "react";
import {
  ANSWER_COLORS,
  QuizShowState,
} from "../pages/Games/quizShowShared";

interface MultiplayerPlayerViewProps {
  roomCode: string;
  displayName: string;
  playerId: string;
  state: QuizShowState | null;
  isOpen: boolean;
  send: (data: unknown) => void;
}

const MultiplayerPlayerView: React.FC<MultiplayerPlayerViewProps> = ({
  roomCode,
  displayName,
  playerId,
  state,
  isOpen,
  send,
}) => {
  const [lockedChoice, setLockedChoice] = useState<number | null>(null);
  const status = state?.status ?? "waiting";
  const question = state?.question;
  const myScore =
    state?.players.find((player) => player.id === playerId)?.score ?? 0;

  const questionKey = `${question?.index ?? "none"}-${status}`;

  useEffect(() => {
    if (status === "question") {
      setLockedChoice(null);
    }
  }, [questionKey, status]);

  const handleAnswer = (choiceIndex: number) => {
    if (status !== "question" || lockedChoice !== null) {
      return;
    }
    setLockedChoice(choiceIndex);
    send({ type: "answer", choiceIndex });
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-lg flex-col p-4 text-black">
      <p className="text-sm uppercase tracking-wide">Room {roomCode}</p>
      <p className="mb-4 text-xl font-bold">
        {displayName} · {myScore} {myScore === 1 ? "point" : "points"}
      </p>
      {!isOpen && <p className="mb-4">Connecting...</p>}

      {status === "waiting" && (
        <p className="text-2xl">You are in. Watch the class screen.</p>
      )}

      {status === "question" && question && (
        <>
          <p className="mb-2 text-sm">
            Question {question.index + 1} of {question.total}
          </p>
          <p className="mb-6 text-2xl font-bold">{question.prompt}</p>
          <div className="grid grid-cols-1 gap-3">
            {question.choices.map((choice, index) => (
              <button
                key={`${choice}-${index}`}
                type="button"
                onClick={() => handleAnswer(index)}
                disabled={lockedChoice !== null}
                className={`min-h-16 rounded border border-black px-4 py-4 text-xl font-bold ${
                  ANSWER_COLORS[index % ANSWER_COLORS.length].className
                } ${lockedChoice !== null && lockedChoice !== index ? "opacity-50" : ""}`}
              >
                {choice}
              </button>
            ))}
          </div>
          {lockedChoice !== null && (
            <p className="mt-4 text-center text-lg">Answer locked in.</p>
          )}
        </>
      )}

      {status === "reveal" && question && (
        <div className="text-center">
          <p className="mb-4 text-xl">{question.prompt}</p>
          <p className="text-3xl font-bold text-green-700">
            {question.correctIndex !== undefined
              ? question.choices[question.correctIndex]
              : "Look at the class screen"}
          </p>
        </div>
      )}

      {status === "ended" && (
        <div>
          <h2 className="mb-4 text-3xl font-bold">Final scores</h2>
          <ol className="space-y-2 text-xl">
            {(state?.players ?? []).map((player, index) => (
              <li key={player.id} className="flex justify-between">
                <span>
                  {index + 1}. {player.displayName}
                </span>
                <span>{player.score}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default MultiplayerPlayerView;
